import { Service, TechCategory } from "./types";
import { 
  PuzzleIcon, 
  FileTextIcon, 
  LightbulbIcon, 
  BarChart3Icon, 
  SettingsIcon, 
  Users2Icon,
  Monitor, 
  Smartphone, 
  Server, 
  Cloud,
  Code,
  Database,
  Cpu
} from "lucide-react";
import React from 'react';

// Services data
export const services: Service[] = [
   {
    title: "Custom AI Agents",
    description:
      "Tailored AI agents designed for your business workflows, improving efficiency, accuracy, and scalability.",
    icon: React.createElement(LightbulbIcon, { className: "h-8 w-8 text-primary-600" }),
  },
 
  {
    title: "Conversational AI Chatbots",
    description:
      "Intelligent AI chatbots that provide 24/7 support, boost customer satisfaction, and improve B2B/B2C conversions.",
    icon: React.createElement(FileTextIcon, { className: "h-8 w-8 text-primary-600" }),
  },
   {
    title: "AI-Optimized Websites & Mobile Apps",
    description:
      "Next-gen websites and mobile apps powered by AI for better visibility, higher engagement, and increased lead generation.",
    icon: React.createElement(PuzzleIcon, { className: "h-8 w-8 text-primary-600" }),
  },
 
  {
    title: "AI-Powered Document Intelligence",
    description:
      "RAG pipelines and smart AI tools to process, analyze, and extract insights from documents quickly and accurately.",
    icon: React.createElement(BarChart3Icon, { className: "h-8 w-8 text-primary-600" }),
  },
  {
    title: "AI-Powered Smart Analytics Suite",
    description:
      "Transform data into actionable insights with AI analytics for faster decisions and smarter strategies.",
    icon: React.createElement(SettingsIcon, { className: "h-8 w-8 text-primary-600" }),
  },
  {
    title: "AI-Powered Marketplaces",
    description:
      "Intelligent B2B and B2C platforms that deliver personalized recommendations and optimize transactions.",
    icon: React.createElement(Users2Icon, { className: "h-8 w-8 text-primary-600" }),
  },
];



// Helper function to create tech items with different colors
const createTechItems = (names: string[], colorClass: string) => {
  return names.map(name => ({ name, color: colorClass }));
};

// Technologies data
export const techCategories: TechCategory[] = [
   {
  icon: React.createElement(Cpu, { className: "h-6 w-6" }),
  name: "Artificial Intelligence",
  description: "Build intelligent solutions with cutting-edge AI technologies",
  technologies: [
    ...createTechItems(["Agentic AI"], "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"),
    ...createTechItems(["RAG", "AI Chatbots"], "bg-blue-100 text-blue-800 hover:bg-blue-200"),
    ...createTechItems(["Generative AI", "Large Language Models"], "bg-orange-100 text-orange-800 hover:bg-orange-200"),
    ...createTechItems(["Prompt Engineering"], "bg-green-100 text-green-800 hover:bg-green-200"),
  ]
},
{
  icon: React.createElement(Database, { className: "h-6 w-6" }),
  name: "Data Science ",
  description: "Leverage advanced data science and machine learning techniques for your business",
  technologies: [
    ...createTechItems(["Predictive Models","Customer Segmentation","Recommedation Engine","Machine Learning", "Deep Learning"], "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"),
    ...createTechItems(["Natural Language Processing", "Computer Vision"], "bg-blue-100 text-blue-800 hover:bg-blue-200"),
    ...createTechItems(["TensorFlow", "PyTorch"], "bg-orange-100 text-orange-800 hover:bg-orange-200"),
    ...createTechItems(["Data Mining", "Data Visualization"], "bg-green-100 text-green-800 hover:bg-green-200"),
    ...createTechItems(["Predictive Analytics", "Big Data"], "bg-purple-100 text-purple-800 hover:bg-purple-200"),
    ...createTechItems(["ETL Pipelines", "Hadoop", "Spark"], "bg-red-100 text-red-800 hover:bg-red-200")
  ]
},
  {
    icon: React.createElement(Monitor, { className: "h-6 w-6" }),
    name: "Web App Development",
    description: "Modern, responsive web applications built with cutting-edge technologies",
    technologies: [
      ...createTechItems(["React", "Next.js", "TypeScript"], "bg-blue-100 text-blue-800 hover:bg-blue-200"),
      ...createTechItems(["Angular", "Vue.js"], "bg-green-100 text-green-800 hover:bg-green-200"),
      ...createTechItems(["Node.js", "Express"], "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"),
      ...createTechItems(["Django", "Ruby on Rails"], "bg-red-100 text-red-800 hover:bg-red-200"),
      ...createTechItems(["GraphQL", "REST APIs"], "bg-purple-100 text-purple-800 hover:bg-purple-200"),
      ...createTechItems(["MongoDB", "PostgreSQL"], "bg-yellow-100 text-yellow-800 hover:bg-yellow-200")
    ]
  },
  {
    icon: React.createElement(Smartphone, { className: "h-6 w-6" }),
    name: "Mobile App Development",
    description: "Cross-platform and native mobile applications for iOS and Android",
    technologies: [
      ...createTechItems(["React Native", "Flutter"], "bg-cyan-100 text-cyan-800 hover:bg-cyan-200"),
      ...createTechItems(["Swift", "iOS Development"], "bg-orange-100 text-orange-800 hover:bg-orange-200"),
      ...createTechItems(["Kotlin", "Android Development"], "bg-green-100 text-green-800 hover:bg-green-200"),
      ...createTechItems(["Mobile UI/UX", "Firebase"], "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"),
      ...createTechItems(["Push Notifications"], "bg-purple-100 text-purple-800 hover:bg-purple-200")
    ]
  },
  {
    icon: React.createElement(Server, { className: "h-6 w-6" }),
    name: "DevOps",
    description: "Streamline your development workflow with modern DevOps practices",
    technologies: [
      ...createTechItems(["Docker", "Kubernetes"], "bg-blue-100 text-blue-800 hover:bg-blue-200"),
      ...createTechItems(["Jenkins", "GitHub Actions", "GitLab CI/CD"], "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"),
      ...createTechItems(["Terraform", "Ansible"], "bg-purple-100 text-purple-800 hover:bg-purple-200"),
      ...createTechItems(["Prometheus", "Grafana", "ELK Stack"], "bg-orange-100 text-orange-800 hover:bg-orange-200"),
      ...createTechItems(["Continuous Integration", "Continuous Deployment"], "bg-green-100 text-green-800 hover:bg-green-200")
    ]
  },
  {
    icon: React.createElement(Cloud, { className: "h-6 w-6" }),
    name: "Cloud Services",
    description: "Scalable cloud solutions designed for modern businesses",
    technologies: [
      ...createTechItems(["Amazon Web Services"], "bg-orange-100 text-orange-800 hover:bg-orange-200"),
      ...createTechItems(["Microsoft Azure"], "bg-blue-100 text-blue-800 hover:bg-blue-200"),
      ...createTechItems(["Google Cloud"], "bg-red-100 text-red-800 hover:bg-red-200"),
      ...createTechItems(["Digital Ocean", "Heroku"], "bg-cyan-100 text-cyan-800 hover:bg-cyan-200"),
      ...createTechItems(["Netlify", "Vercel","Hostinger VPS"], "bg-purple-100 text-purple-800 hover:bg-purple-200"),
      ...createTechItems(["Serverless Architecture", "Microservices"], "bg-green-100 text-green-800 hover:bg-green-200"),
      ...createTechItems(["Cloud Security", "Load Balancing", "Auto-scaling"], "bg-yellow-100 text-yellow-800 hover:bg-yellow-200")
    ]
  },




  {
    icon: React.createElement(Code, { className: "h-6 w-6" }),
    name: "Blockchain",
    description: "Build secure and decentralized applications with blockchain technology",
    technologies: [
      ...createTechItems(["Smart Contracts", "Solidity"], "bg-blue-100 text-blue-800 hover:bg-blue-200"),
      ...createTechItems(["Ethereum", "Web3.js"], "bg-purple-100 text-purple-800 hover:bg-purple-200"),
      ...createTechItems(["Hardhat", "NFTs", "DeFi"], "bg-pink-100 text-pink-800 hover:bg-pink-200"),
      ...createTechItems(["Blockchain Security"], "bg-red-100 text-red-800 hover:bg-red-200"),
      ...createTechItems(["Consensus Mechanisms", "Distributed Systems"], "bg-green-100 text-green-800 hover:bg-green-200")
    ]
  },
];


//countries data

export const COUNTRIES = [

  { value: "af", label: "Afghanistan" },
  { value: "al", label: "Albania" },
  { value: "dz", label: "Algeria" },
  { value: "ad", label: "Andorra" },
  { value: "ao", label: "Angola" },
  { value: "ag", label: "Antigua and Barbuda" },
  { value: "ar", label: "Argentina" },
  { value: "am", label: "Armenia" },
  { value: "au", label: "Australia" },
  { value: "at", label: "Austria" },
  { value: "az", label: "Azerbaijan" },
  { value: "bs", label: "Bahamas" },
  { value: "bh", label: "Bahrain" },
  { value: "bd", label: "Bangladesh" },
  { value: "bb", label: "Barbados" },
  { value: "by", label: "Belarus" },
  { value: "be", label: "Belgium" },
  { value: "bz", label: "Belize" },
  { value: "bj", label: "Benin" },
  { value: "bt", label: "Bhutan" },
  { value: "bo", label: "Bolivia" },
  { value: "ba", label: "Bosnia and Herzegovina" },
  { value: "bw", label: "Botswana" },
  { value: "br", label: "Brazil" },
  { value: "bn", label: "Brunei" },
  { value: "bg", label: "Bulgaria" },
  { value: "bf", label: "Burkina Faso" },
  { value: "bi", label: "Burundi" },
  { value: "cv", label: "Cabo Verde" },
  { value: "kh", label: "Cambodia" },
  { value: "cm", label: "Cameroon" },
  { value: "ca", label: "Canada" },
  { value: "cf", label: "Central African Republic" },
  { value: "td", label: "Chad" },
  { value: "cl", label: "Chile" },
  { value: "cn", label: "China" },
  { value: "co", label: "Colombia" },
  { value: "km", label: "Comoros" },
  { value: "cg", label: "Congo" },
  { value: "cd", label: "Congo (Democratic Republic)" },
  { value: "cr", label: "Costa Rica" },
  { value: "ci", label: "Côte d'Ivoire" },
  { value: "hr", label: "Croatia" },
  { value: "cu", label: "Cuba" },
  { value: "cy", label: "Cyprus" },
  { value: "cz", label: "Czech Republic" },
  { value: "dk", label: "Denmark" },
  { value: "dj", label: "Djibouti" },
  { value: "dm", label: "Dominica" },
  { value: "do", label: "Dominican Republic" },
  { value: "ec", label: "Ecuador" },
  { value: "eg", label: "Egypt" },
  { value: "sv", label: "El Salvador" },
  { value: "gq", label: "Equatorial Guinea" },
  { value: "er", label: "Eritrea" },
  { value: "ee", label: "Estonia" },
  { value: "sz", label: "Eswatini" },
  { value: "et", label: "Ethiopia" },
  { value: "fj", label: "Fiji" },
  { value: "fi", label: "Finland" },
  { value: "fr", label: "France" },
  { value: "ga", label: "Gabon" },
  { value: "gm", label: "Gambia" },
  { value: "ge", label: "Georgia" },
  { value: "de", label: "Germany" },
  { value: "gh", label: "Ghana" },
  { value: "gr", label: "Greece" },
  { value: "gd", label: "Grenada" },
  { value: "gt", label: "Guatemala" },
  { value: "gn", label: "Guinea" },
  { value: "gw", label: "Guinea-Bissau" },
  { value: "gy", label: "Guyana" },
  { value: "ht", label: "Haiti" },
  { value: "hn", label: "Honduras" },
  { value: "hu", label: "Hungary" },
  { value: "is", label: "Iceland" },
  { value: "in", label: "India" },
  { value: "id", label: "Indonesia" },
  { value: "ir", label: "Iran" },
  { value: "iq", label: "Iraq" },
  { value: "ie", label: "Ireland" },
  { value: "il", label: "Israel" },
  { value: "it", label: "Italy" },
  { value: "jm", label: "Jamaica" },
  { value: "jp", label: "Japan" },
  { value: "jo", label: "Jordan" },
  { value: "kz", label: "Kazakhstan" },
  { value: "ke", label: "Kenya" },
  { value: "ki", label: "Kiribati" },
  { value: "kp", label: "Korea (North)" },
  { value: "kr", label: "Korea (South)" },
  { value: "xk", label: "Kosovo" },
  { value: "kw", label: "Kuwait" },
  { value: "kg", label: "Kyrgyzstan" },
  { value: "la", label: "Laos" },
  { value: "lv", label: "Latvia" },
  { value: "lb", label: "Lebanon" },
  { value: "ls", label: "Lesotho" },
  { value: "lr", label: "Liberia" },
  { value: "ly", label: "Libya" },
  { value: "li", label: "Liechtenstein" },
  { value: "lt", label: "Lithuania" },
  { value: "lu", label: "Luxembourg" },
  { value: "mg", label: "Madagascar" },
  { value: "mw", label: "Malawi" },
  { value: "my", label: "Malaysia" },
  { value: "mv", label: "Maldives" },
  { value: "ml", label: "Mali" },
  { value: "mt", label: "Malta" },
  { value: "mh", label: "Marshall Islands" },
  { value: "mr", label: "Mauritania" },
  { value: "mu", label: "Mauritius" },
  { value: "mx", label: "Mexico" },
  { value: "fm", label: "Micronesia" },
  { value: "md", label: "Moldova" },
  { value: "mc", label: "Monaco" },
  { value: "mn", label: "Mongolia" },
  { value: "me", label: "Montenegro" },
  { value: "ma", label: "Morocco" },
  { value: "mz", label: "Mozambique" },
  { value: "mm", label: "Myanmar" },
  { value: "na", label: "Namibia" },
  { value: "nr", label: "Nauru" },
  { value: "np", label: "Nepal" },
  { value: "nl", label: "Netherlands" },
  { value: "nz", label: "New Zealand" },
  { value: "ni", label: "Nicaragua" },
  { value: "ne", label: "Niger" },
  { value: "ng", label: "Nigeria" },
  { value: "mk", label: "North Macedonia" },
  { value: "no", label: "Norway" },
  { value: "om", label: "Oman" },
  { value: "pk", label: "Pakistan" },
  { value: "pw", label: "Palau" },
  { value: "ps", label: "Palestine" },
  { value: "pa", label: "Panama" },
  { value: "pg", label: "Papua New Guinea" },
  { value: "py", label: "Paraguay" },
  { value: "pe", label: "Peru" },
  { value: "ph", label: "Philippines" },
  { value: "pl", label: "Poland" },
  { value: "pt", label: "Portugal" },
  { value: "qa", label: "Qatar" },
  { value: "ro", label: "Romania" },
  { value: "ru", label: "Russia" },
  { value: "rw", label: "Rwanda" },
  { value: "kn", label: "Saint Kitts and Nevis" },
  { value: "lc", label: "Saint Lucia" },
  { value: "vc", label: "Saint Vincent and the Grenadines" },
  { value: "ws", label: "Samoa" },
  { value: "sm", label: "San Marino" },
  { value: "st", label: "Sao Tome and Principe" },
  { value: "sa", label: "Saudi Arabia" },
  { value: "sn", label: "Senegal" },
  { value: "rs", label: "Serbia" },
  { value: "sc", label: "Seychelles" },
  { value: "sl", label: "Sierra Leone" },
  { value: "sg", label: "Singapore" },
  { value: "sk", label: "Slovakia" },
  { value: "si", label: "Slovenia" },
  { value: "sb", label: "Solomon Islands" },
  { value: "so", label: "Somalia" },
  { value: "za", label: "South Africa" },
  { value: "ss", label: "South Sudan" },
  { value: "es", label: "Spain" },
  { value: "lk", label: "Sri Lanka" },
  { value: "sd", label: "Sudan" },
  { value: "sr", label: "Suriname" },
  { value: "se", label: "Sweden" },
  { value: "ch", label: "Switzerland" },
  { value: "sy", label: "Syria" },
  { value: "tw", label: "Taiwan" },
  { value: "tj", label: "Tajikistan" },
  { value: "tz", label: "Tanzania" },
  { value: "th", label: "Thailand" },
  { value: "tl", label: "Timor-Leste" },
  { value: "tg", label: "Togo" },
  { value: "to", label: "Tonga" },
  { value: "tt", label: "Trinidad and Tobago" },
  { value: "tn", label: "Tunisia" },
  { value: "tr", label: "Turkey" },
  { value: "tm", label: "Turkmenistan" },
  { value: "tv", label: "Tuvalu" },
  { value: "ug", label: "Uganda" },
  { value: "ua", label: "Ukraine" },
  { value: "ae", label: "United Arab Emirates" },
  { value: "gb", label: "United Kingdom" },
  { value: "us", label: "United States" },
  { value: "uy", label: "Uruguay" },
  { value: "uz", label: "Uzbekistan" },
  { value: "vu", label: "Vanuatu" },
  { value: "va", label: "Vatican City" },
  { value: "ve", label: "Venezuela" },
  { value: "vn", label: "Vietnam" },
  { value: "ye", label: "Yemen" },
  { value: "zm", label: "Zambia" },
  { value: "zw", label: "Zimbabwe" }
];






